import {
  Card,
  CardContent,
  Avatar,
  Typography,
  IconButton,
  Box,
  Chip,
} from '@mui/material'
import { Edit, Delete, EmojiEvents } from '@mui/icons-material'
import type { FamilyMember } from '@/types'

interface FamilyMemberCardProps {
  member: FamilyMember
  onEdit: (member: FamilyMember) => void
  onDelete: (id: string) => void
}

export default function FamilyMemberCard({
  member,
  onEdit,
  onDelete,
}: FamilyMemberCardProps) {
  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${member.color} 0%, ${member.color}99 100%)`,
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={2.5}>
          <Avatar
            src={member.avatar_url || undefined}
            sx={{
              width: 72,
              height: 72,
              bgcolor: member.color,
              fontSize: '1.75rem',
              border: '4px solid',
              borderColor: `${member.color}22`,
              boxShadow: `0 8px 16px ${member.color}33`,
            }}
          >
            {member.name[0]}
          </Avatar>

          <Box flex={1}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: '1.125rem',
              }}
            >
              {member.name}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label={member.age_category}
                size="small"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
              />
              <Chip
                icon={<EmojiEvents sx={{ color: '#fbbf24 !important' }} />}
                label={`${member.points} pts`}
                size="small"
                sx={{
                  bgcolor: 'secondary.main',
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'secondary.dark',
                  },
                }}
              />
              <Chip
                label={`${member.streak} 🔥`}
                size="small"
                sx={{
                  bgcolor: member.streak > 0 ? '#10b981' : '#e2e8f0',
                  color: member.streak > 0 ? 'white' : '#64748b',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: member.streak > 0 ? '#059669' : '#cbd5e1',
                  },
                }}
              />
            </Box>
          </Box>

          <Box display="flex" gap={1}>
            <IconButton
              size="medium"
              onClick={() => onEdit(member)}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'scale(1.1) rotate(5deg)',
                },
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="medium"
              onClick={() => onDelete(member.id)}
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'error.dark',
                  transform: 'scale(1.1) rotate(-5deg)',
                },
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
